/**
 * ============================================================================
 * DISPUTE SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all dispute-related API calls
 */

import api, { handleApiError } from './api';

/**
 * Create a dispute
 */
export const createDispute = async (data) => {
  try {
    const response = await api.post('/disputes', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute by ID
 */
export const getDispute = async (disputeId) => {
  try {
    const response = await api.get(`/disputes/${disputeId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * List disputes
 */
export const listDisputes = async (filters = {}) => {
  try {
    const response = await api.get('/disputes', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Respond to dispute
 */
export const respondToDispute = async (disputeId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/respond`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Add evidence to dispute
 */
export const addEvidence = async (disputeId, formData) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/evidence`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Make an offer in dispute
 */
export const makeOffer = async (disputeId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/offer`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Accept offer
 */
export const acceptOffer = async (disputeId, offerId) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/offers/${offerId}/accept`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reject offer
 */
export const rejectOffer = async (disputeId, offerId) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/offers/${offerId}/reject`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request mediation
 */
export const requestMediation = async (disputeId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/request-mediation`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Send message in dispute
 */
export const sendMessage = async (disputeId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/messages`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute messages
 */
export const getMessages = async (disputeId) => {
  try {
    const response = await api.get(`/disputes/${disputeId}/messages`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user dispute summary
 */
export const getUserDisputeSummary = async () => {
  try {
    const response = await api.get('/disputes/summary');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get disputes for transaction
 */
export const getTransactionDisputes = async (transactionId) => {
  try {
    const response = await api.get(`/disputes/transaction/${transactionId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute offers
 */
export const getDisputeOffers = async (disputeId) => {
  try {
    const response = await api.get(`/disputes/${disputeId}/offers`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute evidence
 */
export const getDisputeEvidence = async (disputeId) => {
  try {
    const response = await api.get(`/disputes/${disputeId}/evidence`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete evidence
 */
export const deleteEvidence = async (disputeId, evidenceId) => {
  try {
    const response = await api.delete(`/disputes/${disputeId}/evidence/${evidenceId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Respond to offer
 */
export const respondToOffer = async (disputeId, offerId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/offers/${offerId}/respond`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Withdraw dispute
 */
export const withdrawDispute = async (disputeId, data) => {
  try {
    const response = await api.post(`/disputes/${disputeId}/withdraw`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get dispute statistics (admin)
 */
export const getDisputeStats = async (filters = {}) => {
  try {
    const response = await api.get('/disputes/admin/statistics', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  createDispute,
  getDispute,
  listDisputes,
  getUserDisputeSummary,
  getTransactionDisputes,
  respondToDispute,
  addEvidence,
  getDisputeEvidence,
  deleteEvidence,
  makeOffer,
  getDisputeOffers,
  respondToOffer,
  acceptOffer,
  rejectOffer,
  requestMediation,
  sendMessage,
  getMessages,
  withdrawDispute,
  getDisputeStats,
};
